import { uiToInternal, unitsToUiDegrees } from '../shared/angle.js';
import { DEFAULT_CONFIG } from '../shared/config.js';
import { PLAYER_COLOR_CSS } from '../shared/colors.js';
import type { GameSnapshot, ServerMessage, WaitingPlayer } from '../shared/protocol.js';
import { AngleControl } from './angleControl.js';
import { NetClient } from './net.js';
import { Renderer } from './render.js';
import { SinglePlayerSession } from './singlePlayer.js';
import { SpaceBackground } from './spaceBg.js';
import { isTouchUi, TUTORIAL_STEPS, type TutorialVariant } from './tutorial.js';

const SESSION_KEY = 'nw_session_token';
const NAME_KEY = 'nw_display_name';

const net = new NetClient();
const angle = new AngleControl();
let velocity = 10;
let myPlayerId: string | null = null;
let mySlot = 0;
let sessionToken: string | null = localStorage.getItem(SESSION_KEY);
let snapshot: GameSnapshot | null = null;
let youAreCreator = false;
let currentCode: string | null = null;
let energy = 20;
let viewMode: 'home' | 'join' | 'setup' | 'waiting' | 'game' | 'error' = 'home';
let lastPhase: string | null = null;
let sp: SinglePlayerSession | null = null;

const el = {
  home: $('view-home'),
  join: $('view-join'),
  setup: $('view-setup'),
  waiting: $('view-waiting'),
  game: $('view-game'),
  error: $('view-error'),
  setupNameInput: $('setup-name-input') as HTMLInputElement,
  joinNameInput: $('join-name-input') as HTMLInputElement,
  joinCode: $('join-code'),
  joinForm: $('join-form') as HTMLFormElement,
  setupForm: $('setup-form') as HTMLFormElement,
  btnNewGame: $('btn-new-game'),
  btnSinglePlayer: $('btn-single-player'),
  btnSetupBack: $('btn-setup-back'),
  maxPlayers: $('max-players') as HTMLInputElement,
  maxPlayersValue: $('max-players-value'),
  isPublic: $('is-public') as HTMLInputElement,
  planetCount: $('planet-count') as HTMLInputElement,
  planetCountValue: $('planet-count-value'),
  lobbyList: $('lobby-list'),
  lobbyEmpty: $('lobby-empty'),
  lobbyStatus: $('lobby-status'),
  waitCode: $('wait-code'),
  waitUrl: $('wait-url'),
  waitPlayers: $('wait-players'),
  waitHint: $('wait-hint'),
  btnStart: $('btn-start'),
  copyLink: $('copy-link'),
  canvas: $('game-canvas') as HTMLCanvasElement,
  angleDisplay: $('angle-display'),
  velocitySlider: $('velocity-slider') as HTMLInputElement,
  velocityValue: $('velocity-value'),
  energyFill: $('energy-fill'),
  energyText: $('energy-text'),
  btnFire: $('btn-fire'),
  scoreboard: $('scoreboard'),
  hudTimer: $('hud-timer'),
  killFeed: $('kill-feed'),
  boardOverlay: $('board-overlay'),
  boardList: $('board-list'),
  boardCountdown: $('board-countdown'),
  errorText: $('error-text'),
  codeJoinForm: $('code-join-form') as HTMLFormElement,
  codeJoinInput: $('code-join-input') as HTMLInputElement,
  codeJoinError: $('code-join-error'),
  tutorialOverlay: $('tutorial-overlay'),
  tutorialTitle: $('tutorial-title'),
  tutorialBody: $('tutorial-body'),
  tutorialEmphasis: $('tutorial-emphasis'),
  tutorialSkip: $('tutorial-skip'),
  tutorialNext: $('tutorial-next'),
  tutorialStepNum: $('tutorial-step-num'),
  tutorialStepTotal: $('tutorial-step-total'),
};

function $(id: string): HTMLElement {
  const n = document.getElementById(id);
  if (!n) throw new Error(`#${id} missing`);
  return n;
}

function show(view: typeof viewMode): void {
  if (view !== 'game') stopSinglePlayer();
  viewMode = view;
  el.home.classList.toggle('hidden', view !== 'home');
  el.join.classList.toggle('hidden', view !== 'join');
  el.setup.classList.toggle('hidden', view !== 'setup');
  el.waiting.classList.toggle('hidden', view !== 'waiting');
  el.game.classList.toggle('hidden', view !== 'game');
  el.error.classList.toggle('hidden', view !== 'error');
  spaceBg.setParallax(view === 'game' ? 0.1 : 1);
  if (view === 'game') {
    void lockPortrait();
    requestAnimationFrame(() => renderer.resize());
  } else {
    void unlockPortrait();
  }
}

async function lockPortrait(): Promise<void> {
  try {
    const orient = screen.orientation as ScreenOrientation & {
      lock?: (orientation: string) => Promise<void>;
    };
    await orient.lock?.('portrait');
  } catch {
    /* Browser may require fullscreen / may not support lock */
  }
}

async function unlockPortrait(): Promise<void> {
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* ignore */
  }
}

function stopSinglePlayer(): void {
  if (!sp) return;
  sp.stop();
  sp = null;
  renderer.setSinglePlayer(false);
  hideTutorial();
}

let tutorialStep = 0;
let tutorialPendingStart: (() => void) | null = null;

function hideTutorial(): void {
  el.tutorialOverlay.classList.add('hidden');
  tutorialPendingStart = null;
  tutorialStep = 0;
}

function renderTutorialVariant(variant: TutorialVariant): void {
  el.tutorialBody.replaceChildren();

  const lead = document.createElement('p');
  lead.className = 'tutorial-lead';
  lead.textContent = variant.lead;
  el.tutorialBody.append(lead);

  if (variant.rows?.length) {
    const list = document.createElement('ul');
    list.className = 'tutorial-rows';
    for (const row of variant.rows) {
      const li = document.createElement('li');
      const keys = document.createElement('span');
      keys.className = 'tutorial-keys';
      for (const key of row.keys) {
        const kbd = document.createElement('kbd');
        kbd.textContent = key;
        keys.append(kbd);
      }
      const action = document.createElement('span');
      action.className = 'tutorial-action';
      action.textContent = row.action;
      li.append(keys, action);
      list.append(li);
    }
    el.tutorialBody.append(list);
  }

  if (variant.tip) {
    const tip = document.createElement('p');
    tip.className = 'tutorial-tip';
    tip.textContent = variant.tip;
    el.tutorialBody.append(tip);
  }
}

function renderTutorialStep(): void {
  const touch = isTouchUi();
  const step = TUTORIAL_STEPS[tutorialStep]!;
  el.tutorialTitle.textContent = step.title;
  renderTutorialVariant(touch ? step.mobile : step.desktop);
  const last = tutorialStep === TUTORIAL_STEPS.length - 1;
  el.tutorialEmphasis.hidden = !last;
  el.tutorialEmphasis.textContent = last
    ? 'Remember: longer successful shots give more points.'
    : '';
  el.tutorialSkip.hidden = last;
  el.tutorialStepNum.textContent = String(tutorialStep + 1);
  el.tutorialStepTotal.textContent = String(TUTORIAL_STEPS.length);
  el.tutorialNext.textContent = last ? 'Start practice' : 'Next';
}

function openTutorial(onDone: () => void): void {
  tutorialStep = 0;
  tutorialPendingStart = onDone;
  el.tutorialOverlay.classList.remove('hidden');
  renderTutorialStep();
}

function advanceTutorial(): void {
  if (tutorialStep < TUTORIAL_STEPS.length - 1) {
    tutorialStep++;
    renderTutorialStep();
    return;
  }
  const done = tutorialPendingStart;
  hideTutorial();
  done?.();
}

function skipTutorial(): void {
  const done = tutorialPendingStart;
  hideTutorial();
  done?.();
}

function beginPracticeSession(): void {
  stopSinglePlayer();
  myPlayerId = 'sp-you';
  mySlot = 0;
  lastPhase = null;
  energy = DEFAULT_CONFIG.initialEnergy;
  velocity = DEFAULT_CONFIG.defaultVelocity;
  el.velocitySlider.value = String(velocity);
  el.velocityValue.textContent = String(velocity);
  angle.setUnits(0);
  refreshAngleUi();

  const session = new SinglePlayerSession();
  sp = session;
  renderer.setSinglePlayer(true);
  renderer.wipeRoundVisuals();

  session.onUpdate = (snap, events, missileTrails) => {
    snapshot = snap;
    const me = snap.players.find((p) => p.slot === 0);
    if (me) energy = me.energy;

    if (snap.phase === 'board' && lastPhase !== 'board') renderer.wipeRoundVisuals();
    if (snap.phase === 'playing' && lastPhase === 'board') renderer.wipeRoundVisuals();
    lastPhase = snap.phase;

    renderer.syncSpMissileTrails(missileTrails);
    for (const end of events.missileEnds) {
      renderer.endSpTrail(end.id, end.x, end.y);
    }
    for (const impact of events.planetImpacts) {
      renderer.endSpTrail(impact.missileId, impact.x, impact.y);
      renderer.spawnPlanetImpact(impact.x, impact.y);
    }

    for (const k of events.kills) {
      if (k.victimSlot === 0) continue;
      renderer.addDeathMark(k.x, k.y, k.victimSlot);
      if (k.trickshot !== 'none') renderer.addTrickshotBanner(k.x, k.y, k.trickshot);
      el.killFeed.textContent = `+${k.points}`;
    }
    updateHud();
  };

  session.start();
}

function startSinglePlayer(): void {
  stopSinglePlayer();
  // Detach from any multiplayer room so WS reconnect can't steal the practice session
  if (currentCode) {
    net.send({ type: 'leave' });
    currentCode = null;
  }
  myPlayerId = 'sp-you';
  mySlot = 0;
  snapshot = null;
  renderer.setSinglePlayer(true);
  renderer.wipeRoundVisuals();
  show('game');
  history.replaceState(null, '', '/');
  openTutorial(() => beginPracticeSession());
}

function gameCodeFromPath(): string | null {
  const m = location.pathname.match(/^\/game-([A-Za-z0-9]{5})$/);
  return m ? m[1].toUpperCase() : null;
}

function displayName(): string {
  const v =
    el.setupNameInput.value.trim() ||
    el.joinNameInput.value.trim() ||
    localStorage.getItem(NAME_KEY) ||
    'Anonymous';
  localStorage.setItem(NAME_KEY, v);
  return v.slice(0, 12);
}

function syncNameInputs(name: string): void {
  el.setupNameInput.value = name;
  el.joinNameInput.value = name;
}

function commitName(from: HTMLInputElement): void {
  const name = (from.value.trim() || 'Anonymous').slice(0, 12);
  syncNameInputs(name);
  localStorage.setItem(NAME_KEY, name);
}

function showNewGameSetup(): void {
  const saved = localStorage.getItem(NAME_KEY);
  if (saved) el.setupNameInput.value = saved;
  el.maxPlayers.value = '4';
  el.maxPlayersValue.textContent = '4';
  el.planetCount.value = '16';
  el.planetCountValue.textContent = '16';
  show('setup');
  queueMicrotask(() => el.setupNameInput.focus());
}

const renderer = new Renderer(el.canvas);
const spaceBg = new SpaceBackground($('space-bg') as HTMLCanvasElement);
spaceBg.start();

function refreshAngleUi(): void {
  el.angleDisplay.innerHTML = angle.renderHtml();
}

angle.onChange((units) => {
  refreshAngleUi();
  if (viewMode !== 'game') return;
  if (sp) sp.setAngle(units);
  else net.send({ type: 'setAngle', angleUnits: units });
});

function setVelocity(v: number): void {
  velocity = Math.max(0, Math.min(50, Math.round(v)));
  el.velocitySlider.value = String(velocity);
  el.velocityValue.textContent = String(velocity);
  if (viewMode !== 'game') return;
  if (sp) sp.setVelocity(velocity);
  else net.send({ type: 'setVelocity', velocity });
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
}

function fire(): void {
  if (viewMode !== 'game') return;
  if (sp) {
    sp.setAngle(angle.units);
    sp.setVelocity(velocity);
    sp.fire();
    return;
  }
  net.send({ type: 'setAngle', angleUnits: angle.units });
  net.send({ type: 'setVelocity', velocity });
  net.send({ type: 'fire' });
}

function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function updateHud(): void {
  if (!snapshot) return;
  el.hudTimer.textContent = formatTime(snapshot.timeRemainSec);
  el.energyFill.style.width = `${Math.min(100, (energy / DEFAULT_CONFIG.energyLimit) * 100)}%`;
  el.energyText.textContent = String(Math.floor(energy));

  if (sp) {
    const me = snapshot.players.find((p) => p.slot === 0);
    const score = me?.points ?? 0;
    el.scoreboard.innerHTML = `<div class="score-row" style="color:#ff2a2a"><span class="score-name">You</span><span class="score-pts">${score}</span></div>`;
  } else {
    el.scoreboard.innerHTML = snapshot.players
      .slice()
      .sort((a, b) => b.points - a.points)
      .map((p) => {
        const color = PLAYER_COLOR_CSS[p.colorSlot % PLAYER_COLOR_CSS.length];
        const you = p.id === myPlayerId ? ' (you)' : '';
        return `<div class="score-row" style="color:${color}"><span class="score-name">${p.name}${you}</span><span class="score-pts">${p.points}</span></div>`;
      })
      .join('');
  }

  const board = snapshot.phase === 'board';
  el.boardOverlay.classList.toggle('hidden', !board);
  if (board) {
    if (sp) {
      const me = snapshot.players.find((p) => p.slot === 0);
      el.boardList.innerHTML = `<li style="color:#ff2a2a">You — ${me?.points ?? 0}</li>`;
    } else {
      el.boardList.innerHTML = snapshot.players
        .slice()
        .sort((a, b) => b.points - a.points)
        .map((p) => `<li style="color:${PLAYER_COLOR_CSS[p.colorSlot % 8]}">${p.name} — ${p.points}</li>`)
        .join('');
    }
    el.boardCountdown.textContent = String(Math.ceil(snapshot.timeRemainSec));
  }
}

function renderWaiting(players: WaitingPlayer[], maxPlayers: number, code: string, creator: boolean): void {
  currentCode = code;
  youAreCreator = creator;
  el.waitCode.textContent = code;
  const url = `${location.origin}/game-${code}`;
  el.waitUrl.textContent = url;
  el.waitPlayers.innerHTML = players
    .map((p) => {
      const mark = p.isCreator ? ' ★' : '';
      const offline = p.connected ? '' : ' (disconnected)';
      const you = p.id === myPlayerId ? ' (you)' : '';
      return `<li>${p.name}${you}${mark}${offline}</li>`;
    })
    .join('');
  el.waitHint.textContent = `Players ${players.length}/${maxPlayers}. Starts automatically when full.`;
  el.btnStart.classList.toggle('hidden', !(creator && players.length >= 2));
  show('waiting');
  history.replaceState(null, '', `/game-${code}`);
}

function renderLobby(
  games: {
    code: string;
    name?: string;
    creatorName?: string;
    playerCount: number;
    maxPlayers: number;
    planetCount?: number;
  }[],
): void {
  el.lobbyStatus.textContent = 'Live';
  el.lobbyEmpty.classList.toggle('hidden', games.length > 0);
  el.lobbyList.innerHTML = games
    .map((g) => {
      const room = g.name || g.code;
      const host = g.creatorName || 'Anonymous';
      const planets = g.planetCount ?? '—';
      return `<tr>
        <td><strong>${room}</strong></td>
        <td>${g.playerCount} / ${g.maxPlayers}</td>
        <td>${planets}</td>
        <td>${host}</td>
        <td class="lobby-join-cell"><button type="button" class="btn ghost" data-join="${g.code}">Join</button></td>
      </tr>`;
    })
    .join('');
}

const ERROR_EN: Record<string, string> = {
  INVALID_CODE: 'Enter a valid 5-character room code.',
  NOT_FOUND: 'That room does not exist or is no longer available.',
  GAME_STARTED: 'This game has already started.',
  FULL: 'This game is full.',
  NOT_CREATOR: 'Only the creator can start the game.',
  NEED_PLAYERS: 'At least 2 players are required.',
  ALREADY_STARTED: 'The game has already started.',
  NO_SESSION: 'Please connect first.',
  NO_ROOM: 'No active game.',
  NOT_IN_GAME: 'You are not in this game.',
  BAD_JSON: 'Invalid message.',
  UNKNOWN: 'Something went wrong. Please try again.',
};

let pendingDeepLink: string | null = gameCodeFromPath();
let reconnectTried = false;

net.onStatus((connected) => {
  if (!connected && (viewMode === 'home' || viewMode === 'waiting' || viewMode === 'join')) {
    el.lobbyStatus.textContent = 'Connection lost — reconnecting…';
  }
  if (connected) {
    net.send({ type: 'hello', sessionToken: sessionToken ?? undefined, displayName: displayName() });
  }
});

net.onMessage((msg: ServerMessage) => {
  switch (msg.type) {
    case 'helloOk':
      sessionToken = msg.sessionToken;
      // Keep practice identity — overwriting it removes the aim indicator
      if (!sp) myPlayerId = msg.playerId;
      localStorage.setItem(SESSION_KEY, sessionToken);
      el.lobbyStatus.textContent = 'Live';
      net.send({ type: 'subscribeLobby' });
      // Resume seat after WS drop (host/player) or silent deep-link reconnect
      if (sp) break;
      if (sessionToken && (viewMode === 'waiting' || viewMode === 'game') && currentCode) {
        net.send({ type: 'reconnect', code: currentCode, sessionToken });
      } else if (pendingDeepLink && sessionToken && !reconnectTried && viewMode === 'join') {
        reconnectTried = true;
        net.send({ type: 'reconnect', code: pendingDeepLink, sessionToken });
      }
      break;
    case 'lobby':
      renderLobby(msg.games);
      break;
    case 'created':
    case 'joined':
      currentCode = msg.code;
      youAreCreator = 'youAreCreator' in msg ? msg.youAreCreator : true;
      if ('slot' in msg) mySlot = msg.slot;
      history.replaceState(null, '', msg.urlPath);
      pendingDeepLink = null;
      break;
    case 'waiting':
      renderWaiting(msg.players, msg.maxPlayers, msg.code, msg.youAreCreator);
      pendingDeepLink = null;
      break;
    case 'gameStart':
    case 'state':
      if (sp) break;
      snapshot = msg.snapshot;
      if (snapshot.phase === 'board' && lastPhase !== 'board') {
        renderer.wipeRoundVisuals();
      }
      if (snapshot.phase === 'playing' && lastPhase === 'board') {
        renderer.wipeRoundVisuals();
      }
      lastPhase = snapshot.phase;
      renderer.syncMissiles(snapshot);
      if (viewMode !== 'game') {
        renderer.wipeRoundVisuals();
        show('game');
      }
      updateHud();
      pendingDeepLink = null;
      break;
    case 'missileSpawn':
      if (sp) break;
      renderer.trails.spawn(
        msg.missile.id,
        msg.colorSlot,
        msg.missile.x,
        msg.missile.y,
        msg.missile.isDebris,
      );
      break;
    case 'missileEnd':
      if (sp) break;
      renderer.trails.end(msg.missileId, msg.x, msg.y);
      break;
    case 'planetImpact':
      if (sp) break;
      renderer.trails.end(msg.missileId, msg.x, msg.y);
      renderer.spawnPlanetImpact(msg.x, msg.y);
      break;
    case 'kill':
      if (sp) break;
      el.killFeed.textContent = `${msg.killerName} → ${msg.victimName}`;
      renderer.addDeathMark(msg.x, msg.y, msg.colorSlot);
      if (msg.trickshot !== 'none') {
        renderer.addTrickshotBanner(msg.x, msg.y, msg.trickshot);
      }
      break;
    case 'you':
      if (sp) break;
      energy = msg.energy;
      mySlot = msg.slot;
      updateHud();
      break;
    case 'playerLeft':
      if (sp) break;
      el.killFeed.textContent =
        msg.reason === 'disconnected' ? `${msg.name} disconnected` : `${msg.name} left the game`;
      break;
    case 'error':
      if (msg.code === 'NOT_IN_GAME') {
        // Expected for fresh link visits — keep the join/name form visible
        break;
      }
      if (
        msg.code === 'NOT_FOUND' ||
        msg.code === 'GAME_STARTED' ||
        msg.code === 'FULL' ||
        msg.code === 'INVALID_CODE'
      ) {
        const text = ERROR_EN[msg.code] ?? msg.message;
        el.errorText.textContent = text;
        el.codeJoinError.textContent = text;
        el.codeJoinError.classList.remove('hidden');
        if (viewMode === 'join' || pendingDeepLink) {
          show('error');
        }
        pendingDeepLink = null;
      } else if (viewMode === 'home') {
        el.lobbyStatus.textContent = ERROR_EN[msg.code] ?? msg.message;
      } else {
        console.warn(msg);
      }
      break;
    case 'gameEnded':
      if (sp) break;
      el.errorText.textContent =
        msg.message ||
        (msg.reason === 'alone'
          ? 'All other players left. The game has closed.'
          : 'This game has ended.');
      show('error');
      break;
  }
});

function showJoinGate(code: string): void {
  pendingDeepLink = code;
  el.joinCode.textContent = code;
  const saved = localStorage.getItem(NAME_KEY);
  if (saved) el.joinNameInput.value = saved;
  show('join');
  queueMicrotask(() => el.joinNameInput.focus());
}

function submitJoin(): void {
  const code = pendingDeepLink || gameCodeFromPath();
  if (!code) return;
  const name = (el.joinNameInput.value.trim() || 'Anonymous').slice(0, 12);
  syncNameInputs(name);
  localStorage.setItem(NAME_KEY, name);
  net.send({ type: 'join', code, displayName: name });
}

el.btnNewGame.addEventListener('click', () => showNewGameSetup());
el.btnSinglePlayer.addEventListener('click', () => startSinglePlayer());
el.btnSetupBack.addEventListener('click', () => show('home'));
el.tutorialNext.addEventListener('click', () => advanceTutorial());
el.tutorialSkip.addEventListener('click', () => skipTutorial());

el.codeJoinForm.addEventListener('submit', (e) => {
  e.preventDefault();
  el.codeJoinError.classList.add('hidden');
  const raw = el.codeJoinInput.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  el.codeJoinInput.value = raw;
  if (raw.length !== 5) {
    el.codeJoinError.textContent = ERROR_EN.INVALID_CODE!;
    el.codeJoinError.classList.remove('hidden');
    return;
  }
  history.pushState(null, '', `/game-${raw}`);
  showJoinGate(raw);
});

el.planetCount.addEventListener('input', () => {
  el.planetCountValue.textContent = el.planetCount.value;
});
el.maxPlayers.addEventListener('input', () => {
  el.maxPlayersValue.textContent = el.maxPlayers.value;
});

el.setupForm.addEventListener('submit', (e) => {
  e.preventDefault();
  const name = (el.setupNameInput.value.trim() || 'Anonymous').slice(0, 12);
  syncNameInputs(name);
  localStorage.setItem(NAME_KEY, name);
  const planetCount = Math.min(25, Math.max(10, Number(el.planetCount.value) || 16));
  const maxPlayers = Math.min(8, Math.max(2, Number(el.maxPlayers.value) || 4));
  net.send({
    type: 'create',
    maxPlayers,
    isPublic: el.isPublic.checked,
    displayName: name,
    planetCount,
  });
});

el.lobbyList.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest('[data-join]') as HTMLElement | null;
  if (!btn) return;
  const code = btn.getAttribute('data-join')!;
  history.pushState(null, '', `/game-${code}`);
  showJoinGate(code);
});

el.joinForm.addEventListener('submit', (e) => {
  e.preventDefault();
  submitJoin();
});

el.btnStart.addEventListener('click', () => net.send({ type: 'start' }));
el.copyLink.addEventListener('click', async () => {
  const url = `${location.origin}/game-${currentCode}`;
  try {
    if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
    await navigator.clipboard.writeText(url);
    el.copyLink.textContent = 'Copied!';
    setTimeout(() => {
      el.copyLink.textContent = 'Copy link';
    }, 1500);
  } catch {
    el.waitUrl.textContent = url;
    el.waitHint.textContent = 'Clipboard unavailable — select and copy the link above.';
  }
});

for (const input of [el.setupNameInput, el.joinNameInput]) {
  input.addEventListener('change', () => commitName(input));
  input.addEventListener('blur', () => commitName(input));
}

el.btnFire.addEventListener('click', () => fire());
el.velocitySlider.addEventListener('input', () => setVelocity(Number(el.velocitySlider.value)));

document.querySelectorAll('[data-angle-digit]').forEach((btn) => {
  btn.addEventListener('click', () => {
    angle.selectDigitDelta(Number((btn as HTMLElement).dataset.angleDigit));
    refreshAngleUi();
  });
});

/** Hold +/− to ramp the selected digit quickly (touch / mouse). */
function bindHoldNudge(btn: HTMLElement): void {
  const dir = Number(btn.dataset.angleDelta) as 1 | -1;
  let delayTimer: ReturnType<typeof setTimeout> | null = null;
  let repeatTimer: ReturnType<typeof setInterval> | null = null;
  let holding = false;

  const clear = () => {
    holding = false;
    if (delayTimer !== null) {
      clearTimeout(delayTimer);
      delayTimer = null;
    }
    if (repeatTimer !== null) {
      clearInterval(repeatTimer);
      repeatTimer = null;
    }
  };

  const step = () => {
    angle.nudge(dir);
  };

  btn.addEventListener('pointerdown', (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try {
      btn.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (holding) return;
    holding = true;
    step();
    delayTimer = setTimeout(() => {
      repeatTimer = setInterval(step, 45);
    }, 280);
  });
  btn.addEventListener('pointerup', clear);
  btn.addEventListener('pointercancel', clear);
  btn.addEventListener('lostpointercapture', clear);
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
}

document.querySelectorAll('[data-angle-delta]').forEach((btn) => {
  bindHoldNudge(btn as HTMLElement);
});

/** Kill iOS/Android double-tap zoom on the game surface (keep controls tappable). */
let lastTouchEnd = 0;
document.addEventListener(
  'touchend',
  (e) => {
    if (viewMode !== 'game') return;
    const now = Date.now();
    if (now - lastTouchEnd < 320) {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('button, input, a, label')) e.preventDefault();
    }
    lastTouchEnd = now;
  },
  { passive: false },
);
document.addEventListener('gesturestart', ((e: Event) => e.preventDefault()) as EventListener);

window.addEventListener(
  'keydown',
  (e) => {
    if (isTypingTarget(e.target)) return;
    if (viewMode === 'game') {
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        angle.selectDigitDelta(-1);
        refreshAngleUi();
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        angle.selectDigitDelta(1);
        refreshAngleUi();
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();
        if (e.shiftKey) {
          angle.nudgeShift(1);
          refreshAngleUi();
        } else angle.nudge(1);
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        if (e.shiftKey) {
          angle.nudgeShift(-1);
          refreshAngleUi();
        } else angle.nudge(-1);
      } else if (e.code === 'Space') {
        e.preventDefault();
        if (!e.repeat) fire();
      } else if (e.key === '+' || e.code === 'NumpadAdd' || (e.code === 'Equal' && e.shiftKey)) {
        e.preventDefault();
        setVelocity(velocity + 1);
      } else if (e.key === '-' || e.code === 'NumpadSubtract' || e.code === 'Minus') {
        e.preventDefault();
        setVelocity(velocity - 1);
      }
    }
  },
  { passive: false },
);

function frame(): void {
  if (viewMode === 'game') {
    const me = sp
      ? snapshot?.players.find((p) => p.slot === 0)
      : snapshot?.players.find((p) => p.id === myPlayerId);
    renderer.draw(
      snapshot,
      me ? { x: me.x, y: me.y, angleInternal: uiToInternal(unitsToUiDegrees(angle.units)) } : undefined,
    );
  }
  requestAnimationFrame(frame);
}

const savedName = localStorage.getItem(NAME_KEY);
if (savedName) syncNameInputs(savedName);
refreshAngleUi();
net.connect();

const deepLink = gameCodeFromPath();
if (deepLink) showJoinGate(deepLink);
else show('home');

requestAnimationFrame(frame);
window.addEventListener('resize', () => {
  if (viewMode === 'game') renderer.resize();
});

window.addEventListener('orientationchange', () => {
  if (viewMode === 'game') {
    void lockPortrait();
    setTimeout(() => renderer.resize(), 120);
  }
});

void mySlot;
void youAreCreator;
void DEFAULT_CONFIG;
